/**
 * Domain error codes — the wire contract.
 *
 * `error.code` in an API envelope is a STABLE MACHINE CODE. It is not a class
 * name and it is not human text. Clients branch on it, so it is as much a
 * published interface as a URL path.
 *
 * ─── Why this file exists ────────────────────────────────────────────────────
 *
 * Every domain error class already declared a code like this one. None of them
 * ever reached a client: `HttpExceptionFilter` emitted `code: err.name` — the
 * CLASS name — so the wire carried `"TalentInvitationEmailMismatchError"` while
 * every frontend branch tested for `"TALENT_INVITATION_EMAIL_MISMATCH"`. The
 * comparison was never true, and nothing anywhere failed:
 *
 *   • journey-web's "Wrong account" card — written, translated into five
 *     locales, never once rendered. A citizen hitting an invitation meant for a
 *     different address saw "Something went wrong" with the body "Forbidden",
 *     which is why one of them retried three times, burned three invitations
 *     and ended up deleting their account.
 *   • the expired and revoked cards on the same page — both fell through to the
 *     generic "we couldn't find that invitation" copy.
 *   • `toGuideStateConflict` in @tonnie/sdk — gated on
 *     `code === 'GUIDE_STATE_CONFLICT'`, so it always returned null and
 *     CoPilot's optimistic-concurrency refetch-and-retry NEVER engaged. A
 *     guide-state write conflict lost the citizen's work behind a generic
 *     error. That one is not an invitation bug at all; it was found by fixing
 *     this contract.
 *
 * A string literal compared against a string literal produced by a different
 * layer is a defect that no type checker and no test caught for months. So the
 * codes live here, both layers import them, and a typo is now a compile error.
 *
 * ─── Rules ──────────────────────────────────────────────────────────────────
 *
 * 1. `SCREAMING_SNAKE`, and the value is always identical to the key. The
 *    duplication is the point: the value is the wire contract and grepping for
 *    either form must find this file.
 * 2. NEVER change an existing value. It is a published contract; renaming one
 *    silently breaks whatever branches on it.
 * 3. A new error class adds its code here and its status to
 *    platform-api's `DOMAIN_ERROR_CATALOG`. `domain-error-catalog.spec.ts`
 *    fails if the two disagree, so neither can be forgotten.
 *
 * The numbered families (`AFFILIATE_001`, `CHECKUP_002`, …) are deliberately
 * left as they are. They are opaque but they are stable and already published;
 * renaming them to speaking codes would be a breaking change with no
 * correctness gain, and it is not what the missing branches needed.
 */

export const DOMAIN_ERROR_CODES = {
  // ── Talent invitations (business/invitations) ──────────────────────────────
  TALENT_INVITATION_NOT_FOUND: 'TALENT_INVITATION_NOT_FOUND',
  TALENT_INVITATION_EXPIRED: 'TALENT_INVITATION_EXPIRED',
  TALENT_INVITATION_REVOKED: 'TALENT_INVITATION_REVOKED',
  TALENT_INVITATION_ALREADY_ACCEPTED: 'TALENT_INVITATION_ALREADY_ACCEPTED',
  TALENT_INVITATION_EMAIL_MISMATCH: 'TALENT_INVITATION_EMAIL_MISMATCH',
  TALENT_INVITATION_DECLINED: 'TALENT_INVITATION_DECLINED',
  TALENT_INVITATION_DUPLICATE: 'TALENT_INVITATION_DUPLICATE',
  /**
   * The invited address IS on the caller's account but is not verified.
   *
   * Distinct from MISMATCH on purpose. Both used to throw
   * `TalentInvitationEmailMismatchError`, so a citizen whose OAuth primary
   * happened to be unverified got the identical error and status as someone
   * holding a stranger's invitation — indistinguishable to the UI AND to the
   * logs, which is why "6 mismatch errors in 48h" could not be interpreted.
   * They also need opposite remediations: verify this address vs. this is not
   * your invitation.
   */
  TALENT_INVITATION_EMAIL_UNVERIFIED: 'TALENT_INVITATION_EMAIL_UNVERIFIED',
  /** A business-portal identity acting on a talent invitation. */
  TALENT_INVITATION_WRONG_PORTAL: 'TALENT_INVITATION_WRONG_PORTAL',
  /**
   * The operator's own invitation message was written from the OTHER kind's
   * template — CheckUp text on a TestIt invitation, or the reverse. Refused
   * before it is stored or sent: the email could not be recalled.
   */
  TALENT_INVITATION_MESSAGE_MISMATCH: 'TALENT_INVITATION_MESSAGE_MISMATCH',
  /**
   * D2 — a company's request for more (a CheckUp on top of TestIt, more tests)
   * answered by someone other than the talent it is about. A request is
   * authorised by WHO it is about, never by an address.
   */
  SHARE_REQUEST_NOT_YOURS: 'SHARE_REQUEST_NOT_YOURS',
  /**
   * D2 — a request on a relationship that is no longer live: the citizen left
   * the programme, or the company archived the relationship. Nothing can widen
   * on it; the company asks again once it is live.
   */
  SHARE_REQUEST_RELATIONSHIP_CLOSED: 'SHARE_REQUEST_RELATIONSHIP_CLOSED',
  /**
   * Citizen first run · S5 — claiming an invitation sent to another Tonnie
   * account. NOT_NEEDED: the caller can already act on it, or no other account
   * holds the address (verify it on this account instead). RATE_LIMITED: a code
   * was sent too recently or too often. CODE_INVALID: wrong code, attempts
   * remain. CODE_EXPIRED: no live code — expired, used up, or never sent.
   */
  INVITATION_CLAIM_NOT_NEEDED: 'INVITATION_CLAIM_NOT_NEEDED',
  INVITATION_CLAIM_RATE_LIMITED: 'INVITATION_CLAIM_RATE_LIMITED',
  INVITATION_CLAIM_CODE_INVALID: 'INVITATION_CLAIM_CODE_INVALID',
  INVITATION_CLAIM_CODE_EXPIRED: 'INVITATION_CLAIM_CODE_EXPIRED',
  COHORT_AT_CAPACITY: 'COHORT_AT_CAPACITY',
  PROGRAM_AT_CAPACITY: 'PROGRAM_AT_CAPACITY',

  // ── Journey state machine (platform/journeys) ─────────────────────────────
  // New. These six classes passed the class name as `message` and the human
  // text as `code` — the two arguments of `TonnieError(message, code)` were
  // swapped, and `name` was left as the base's 'TonnieError'. The filter had a
  // heuristic that un-swapped them and therefore worked by accident.
  ILLEGAL_LIFECYCLE_TRANSITION: 'ILLEGAL_LIFECYCLE_TRANSITION',
  ILLEGAL_FUNDING_TRANSITION: 'ILLEGAL_FUNDING_TRANSITION',
  /** A move into sponsored funding named no sponsoring company (R6). */
  SPONSORED_FUNDING_NEEDS_SPONSOR: 'SPONSORED_FUNDING_NEEDS_SPONSOR',
  /** A live relationship on a journey another company sponsors (R6). */
  JOURNEY_SPONSORED_BY_ANOTHER_COMPANY: 'JOURNEY_SPONSORED_BY_ANOTHER_COMPANY',
  /** Sponsoring a journey another company is live on (R6 · D-D). */
  JOURNEY_SHARED_WITH_ANOTHER_COMPANY: 'JOURNEY_SHARED_WITH_ANOTHER_COMPANY',
  PRIMARY_JOURNEY_CONFLICT: 'PRIMARY_JOURNEY_CONFLICT',
  JOURNEY_STATE_NOT_FOUND: 'JOURNEY_STATE_NOT_FOUND',
  JOURNEY_NOT_RESTORABLE: 'JOURNEY_NOT_RESTORABLE',
  /**
   * D-A — the citizen already holds the most live journeys one origin →
   * destination may have. Carries the cap and the journeys on the route so the
   * client can offer to open one instead.
   */
  JOURNEY_ROUTE_CAP_REACHED: 'JOURNEY_ROUTE_CAP_REACHED',
  /**
   * R5 — the journey a citizen chose to share at acceptance is not one they
   * may choose: not theirs, not live, or not on the invitation's destination.
   */
  JOURNEY_NOT_CHOOSABLE: 'JOURNEY_NOT_CHOOSABLE',
  /** R3 — a create request id the citizen already used for a different route. */
  JOURNEY_REQUEST_ID_REUSED: 'JOURNEY_REQUEST_ID_REUSED',
  JOURNEY_ALREADY_ARCHIVED: 'JOURNEY_ALREADY_ARCHIVED',
  /**
   * A journey or an invitation named a destination that is not OPEN — no
   * relocation kit, CheckUp persona or QultureUp world exists for it yet.
   */
  DESTINATION_NOT_OPEN: 'DESTINATION_NOT_OPEN',
  CONSENT_SCOPE_UNKNOWN: 'CONSENT_SCOPE_UNKNOWN',
  /**
   * The citizen tried to switch ON a scope this company never held on the
   * relationship. Turning a scope back on RESTORES a permission the citizen
   * once gave; it is not a way to hand a company data it never asked for.
   */
  CONSENT_SCOPE_NEVER_HELD: 'CONSENT_SCOPE_NEVER_HELD',
  /**
   * D1 — the citizen tried to switch a scope back ON for a relationship that is
   * over (they left the programme). Its consent is closed; the way back is the
   * company's new invitation.
   */
  CONSENT_RELATIONSHIP_ENDED: 'CONSENT_RELATIONSHIP_ENDED',
  /**
   * W8.D — the citizen has not granted this company the scope governing the
   * resource it asked for.
   *
   * It is an ERROR rather than an empty body because of the surfaces it
   * guards: a single resource (a retained file, a presigned URL, one foreign
   * DTO) has no field to hang an `entitled: false` on, and answering `null`
   * there is indistinguishable from "not calculated yet" — a claim about the
   * citizen instead of about their permission. The payload names the scope, so
   * a client renders WHICH consent is missing rather than a generic refusal.
   *
   * Composed DTOs the drawer assembles do NOT use this: they carry a flat
   * `entitled: false` beside nulls (the W3.3 shape), because there the rest of
   * the response is still true and must still render.
   */
  CONSENT_SCOPE_WITHHELD: 'CONSENT_SCOPE_WITHHELD',
  /**
   * W8.1 · F47 — the citizen asked to archive a journey a company still holds.
   *
   * Distinct from `JOURNEY_ALREADY_ARCHIVED` (already in that state) because
   * the remedy is different and the client renders it differently: this one is
   * answered by "leave the programme", which ends the company's relationship
   * (D1) and so lifts the block — not by a retry.
   */
  CITIZEN_JOURNEY_ARCHIVE_BLOCKED: 'CITIZEN_JOURNEY_ARCHIVE_BLOCKED',
  /** W3.2 — a mutation aimed at a relationship the company archived or ended. */
  ENGAGEMENT_ARCHIVED: 'ENGAGEMENT_ARCHIVED',
  /** A talent-keyed company action met several live journeys with this company; it must name one. */
  ENGAGEMENT_JOURNEY_CHOICE_REQUIRED: 'ENGAGEMENT_JOURNEY_CHOICE_REQUIRED',
  /** H17 — a journey that cannot be re-attributed to an invitation. */
  JOURNEY_NOT_ADOPTABLE: 'JOURNEY_NOT_ADOPTABLE',

  // ── Engagement evidence (business/evidence) · W5.8 ────────────────────────
  /** No capture (or item, or blob reference) of that id belongs to the caller. */
  EVIDENCE_NOT_FOUND: 'EVIDENCE_NOT_FOUND',
  /** The retained file is no longer available: redacted, or its retention ran out. */
  EVIDENCE_BLOB_UNAVAILABLE: 'EVIDENCE_BLOB_UNAVAILABLE',
  /** Only a captured (or legacy) item can be redacted, and only once. */
  EVIDENCE_NOT_REDACTABLE: 'EVIDENCE_NOT_REDACTABLE',
  /** The retention-duration configuration row is missing or malformed. */
  EVIDENCE_RETENTION_CONFIG_INVALID: 'EVIDENCE_RETENTION_CONFIG_INVALID',

  // ── CoPilot (platform/copilot) ────────────────────────────────────────────
  GUIDE_STATE_CONFLICT: 'GUIDE_STATE_CONFLICT',
  /** A finished guide's hero result failed its contract — nothing was finalized. */
  GUIDE_RESULT_INVALID: 'GUIDE_RESULT_INVALID',

  // ── Pool import (business/pool) ───────────────────────────────────────────
  POOL_IMPORT_001: 'POOL_IMPORT_001',

  // ── Care (heroes/care) ────────────────────────────────────────────────────
  CARE_OBJ_FEEDBACK_001: 'CARE_OBJ_FEEDBACK_001',
  CARE_OBJ_FEEDBACK_002: 'CARE_OBJ_FEEDBACK_002',
  /** The caller's company does not govern this talent's Care — another company's strategy applies (feedback, targets). */
  CARE_NOT_GOVERNING_COMPANY: 'CARE_NOT_GOVERNING_COMPANY',
  /** A citizen's Care write on a plan that does not include Care (CARE-ASM-03). */
  CARE_PLAN_REQUIRED: 'CARE_PLAN_REQUIRED',

  // ── CheckUp (heroes/checkup) ──────────────────────────────────────────────
  CHECKUP_001: 'CHECKUP_001',
  CHECKUP_002: 'CHECKUP_002',
  CHECKUP_003: 'CHECKUP_003',
  CHECKUP_004: 'CHECKUP_004',
  CHECKUP_005: 'CHECKUP_005',
  CHECKUP_006: 'CHECKUP_006',
  CHECKUP_007: 'CHECKUP_007',
  /** W8.1 · F33 — the profile is not complete enough to generate a score. */
  CHECKUP_008: 'CHECKUP_008',
  // ── L4 region: F13 pitch length ──
  /** F13 — a pitch recording is longer than PITCH_MAX_DURATION_SECONDS. */
  CHECKUP_PITCH_TOO_LONG: 'CHECKUP_PITCH_TOO_LONG',
  /**
   * F13 — nobody can state the pitch's length: the client could not read it
   * (`duration: 0`) and the stored container header carries none.
   */
  CHECKUP_PITCH_UNREADABLE: 'CHECKUP_PITCH_UNREADABLE',
  // ── end L4 region ──

  // ── Identity documents (platform/identity-documents) ──────────────────────
  /** The passport / ID card could not be read — `details.reason` says why. */
  IDENTITY_DOCUMENT_NOT_READ: 'IDENTITY_DOCUMENT_NOT_READ',
  /** No such document (or file) of the caller's, or it is no longer awaiting review. */
  IDENTITY_DOCUMENT_NOT_FOUND: 'IDENTITY_DOCUMENT_NOT_FOUND',

  // ── Account (platform/users) ─────────────────────────────────────────────
  /** The sign-in provider did not take the new name — nothing was changed. */
  ACCOUNT_NAME_NOT_SAVED: 'ACCOUNT_NAME_NOT_SAVED',

  // ── QultureUp (heroes/qultureup) ──────────────────────────────────────────
  QULTUREUP_001: 'QULTUREUP_001',
  QULTUREUP_002: 'QULTUREUP_002',
  /** No assessment attempt by that id belongs to the caller on this journey. */
  QULTUREUP_003: 'QULTUREUP_003',
  /** The attempt was already placed into another assessment — start a new one. */
  QULTUREUP_004: 'QULTUREUP_004',
  /** The question was not asked in that attempt. */
  QULTUREUP_005: 'QULTUREUP_005',

  // ── TelePortal (heroes/teleportal) ────────────────────────────────────────
  TELEPORTAL_001: 'TELEPORTAL_001',
  TELEPORTAL_002: 'TELEPORTAL_002',
  TELEPORTAL_003: 'TELEPORTAL_003',
  /** No relocation kit exists for the journey's destination yet — retrying cannot help. */
  TELEPORTAL_004: 'TELEPORTAL_004',
  /** The kit engine did not answer (timeout, outage) — nothing was saved; try again. */
  TELEPORTAL_005: 'TELEPORTAL_005',

  // ── TestIt (heroes/testit) ────────────────────────────────────────────────
  TESTIT_001: 'TESTIT_001',
  TESTIT_002: 'TESTIT_002',
  TESTIT_003: 'TESTIT_003',
  TESTIT_004: 'TESTIT_004',
  TESTIT_005: 'TESTIT_005',
  /** A Pass/Decline on a relationship that is not a TestIt invitation — a CheckUp one has none. */
  TESTIT_006: 'TESTIT_006',
  /** A TestIt reminder on a relationship that invited no tests — there is nothing to complete. */
  TESTIT_007: 'TESTIT_007',

  // ── Affiliates (platform/billing/affiliates) ──────────────────────────────
  AFFILIATE_001: 'AFFILIATE_001',
  AFFILIATE_002: 'AFFILIATE_002',
  AFFILIATE_003: 'AFFILIATE_003',
  AFFILIATE_004: 'AFFILIATE_004',
  AFFILIATE_005: 'AFFILIATE_005',
  AFFILIATE_006: 'AFFILIATE_006',
  AFFILIATE_007: 'AFFILIATE_007',
  AFFILIATE_008: 'AFFILIATE_008',
  AFFILIATE_009: 'AFFILIATE_009',
  AFFILIATE_010: 'AFFILIATE_010',

  // ── Ad-hoc HttpException codes ────────────────────────────────────────────
  // Emitted by an explicit `throw new HttpException({ code, … })` rather than by
  // a domain error class, so they have no catalog entry — but they are still the
  // wire contract a client branches on, which is what this registry is for.
  REMINDER_TOO_SOON: 'REMINDER_TOO_SOON',
  /**
   * The citizen's daily CoPilot message quota is used up (`PlanGatingGuard`,
   * 429). Its own code so a client can tell it from a generic throttle, which
   * shares the status — "you've reached your daily limit" is only true here.
   */
  COPILOT_QUOTA_EXCEEDED: 'COPILOT_QUOTA_EXCEEDED',
  /**
   * The Vault refused a file's content (400); `data.reason` is a
   * `VaultContentRejectionReason` (`@tonnie/types`).
   */
  VAULT_CONTENT_REJECTED: 'VAULT_CONTENT_REJECTED',
  /**
   * A sponsorship is no longer waiting for the talent's answer — accepted,
   * declined, cancelled or abandoned already — so the offer cannot be answered,
   * resent or abandoned (400).
   */
  SPONSORSHIP_NOT_AWAITING_TALENT: 'SPONSORSHIP_NOT_AWAITING_TALENT',
  /** Team invitations (business-web /accept-invite): no invitation for this link (404). */
  TEAM_INVITATION_NOT_FOUND: 'TEAM_INVITATION_NOT_FOUND',
  /** The team invitation's window closed (410). */
  TEAM_INVITATION_EXPIRED: 'TEAM_INVITATION_EXPIRED',
  /** Someone else already accepted this team invitation (409). */
  TEAM_INVITATION_ALREADY_ACCEPTED: 'TEAM_INVITATION_ALREADY_ACCEPTED',
  /** The invited address is on the account but not verified yet (403). */
  TEAM_INVITATION_EMAIL_UNVERIFIED: 'TEAM_INVITATION_EMAIL_UNVERIFIED',
  /** The invitation was sent to an address this account does not have (403). */
  TEAM_INVITATION_EMAIL_MISMATCH: 'TEAM_INVITATION_EMAIL_MISMATCH',

  /**
   * An account and security message template cannot be switched off, taken off
   * the email channel or deleted (422) — the platform sends it itself.
   */
  ACCOUNT_TEMPLATE_LOCKED: 'ACCOUNT_TEMPLATE_LOCKED',

  // ── Service credentials (business/service-credentials) ────────────────────
  SERVICE_CREDENTIAL_NOT_FOUND: 'SERVICE_CREDENTIAL_NOT_FOUND',
  SERVICE_CREDENTIAL_LIMIT_REACHED: 'SERVICE_CREDENTIAL_LIMIT_REACHED',
  SERVICE_CREDENTIAL_COHORT_NOT_OWNED: 'SERVICE_CREDENTIAL_COHORT_NOT_OWNED',

  // ── Billing (platform/billing) ────────────────────────────────────────────
  BILLING_001: 'BILLING_001',
  BILLING_002: 'BILLING_002',
  BILLING_003: 'BILLING_003',
  BILLING_004: 'BILLING_004',
  /** An invoice the caller may not see, or none by that id — the same 404 either way. */
  BILLING_INVOICE_NOT_FOUND: 'BILLING_INVOICE_NOT_FOUND',
  /** Stripe did not mint the invoice's document link just now — retry. */
  BILLING_INVOICE_DOCUMENT_UNAVAILABLE: 'BILLING_INVOICE_DOCUMENT_UNAVAILABLE',
  // ── L2 region: N6 Pool sponsorship ──
  /** N6 — sponsorship is invite-first: this company has no relationship with the Pool talent. */
  BILLING_POOL_SPONSORSHIP_NEEDS_INVITATION: 'BILLING_POOL_SPONSORSHIP_NEEDS_INVITATION',
  // ── end L2 region ──
  /** Family access on a sponsorship was asked for while the Family add-on is out of production. */
  BILLING_FAMILY_ACCESS_UNAVAILABLE: 'BILLING_FAMILY_ACCESS_UNAVAILABLE',
  /** A paid unlock asked for a journey the person's active Pro already covers. */
  BILLING_JOURNEY_COVERED_BY_PRO: 'BILLING_JOURNEY_COVERED_BY_PRO',
  /**
   * The talent may not renew this journey: a company still sponsors it, or its
   * funding cannot become the talent's own (`isRenewableByTalent`).
   */
  BILLING_JOURNEY_NOT_RENEWABLE: 'BILLING_JOURNEY_NOT_RENEWABLE',
  /** The journey already holds a live sponsorship — one company sponsors a journey, once. */
  BILLING_JOURNEY_ALREADY_SPONSORED: 'BILLING_JOURNEY_ALREADY_SPONSORED',

  // ── L3 region: message templates (hotfix 2026.9.14 · F37) ─────────────────
  MESSAGE_TEMPLATE_TALENT_TAG_WITHOUT_SUBJECT: 'MESSAGE_TEMPLATE_TALENT_TAG_WITHOUT_SUBJECT',
  // ── end L3 region ─────────────────────────────────────────────────────────
} as const;

export type DomainErrorCode = (typeof DOMAIN_ERROR_CODES)[keyof typeof DOMAIN_ERROR_CODES];

/** Every declared code, for the registry tests to iterate. */
export const ALL_DOMAIN_ERROR_CODES = Object.values(DOMAIN_ERROR_CODES) as DomainErrorCode[];
